import { useEffect, useRef } from 'react';
import maintainDisabled from 'ally.js/src/maintain/disabled';
import maintainHidden from 'ally.js/src/maintain/hidden';
import a11yUtils from '@fhs/shared/src/utilities/a11y';

export default function useModalComponent(id, onClose) {
    const CONTAINER_SELECTOR = '.site-modals';
    const modalsContainerRef = useRef(null);
    const modalRef = useRef(null);

    const maintainDisabledHandle = useRef(null);
    const maintainHiddenHandler = useRef(null);

    const titleId = `${id}-title`;

    function handleEntered() {
        a11yUtils.focusFirstFocusableElement(modalRef.current);
        a11yUtils.lockScroll();

        // Disable tab focus except in this modal
        maintainDisabledHandle.current = maintainDisabled({
            context: document.getElementById('root'),
            filter: modalRef.current,
        });

        // Make everything else but the modal aria-hidden
        maintainHiddenHandler.current = maintainHidden({
            context: document.getElementById('root'),
            filter: modalRef.current,
        });
    }

    function handleExited() {
        a11yUtils.unlockScroll();

        // Disengage all a11y features
        if (maintainDisabledHandle.current) {
            maintainDisabledHandle.current.disengage();
        }

        if (maintainHiddenHandler.current) {
            maintainHiddenHandler.current.disengage();
        }
    }

    function handleOverlayClick() {
        onClose();
    }

    // Remove all ally functionality when unmounted between page transitions
    useEffect(() => () => handleExited(), []);

    useEffect(() => {
        const keydownHandler = (event) => {
            // On escape keydown close
            if (event.keyCode === 27) {
                onClose();
            }
        };
        window.addEventListener('keydown', keydownHandler);
        return () => window.removeEventListener('keydown', keydownHandler);
    }, [onClose]);

    // Query for modals container so modals can be portaled
    useEffect(() => {
        const modalContainerMountingElement = document.querySelector(
            CONTAINER_SELECTOR
        );
        if (!modalContainerMountingElement) {
            throw new Error(
                `Modal: No elements found matching: "${CONTAINER_SELECTOR}", Modal component needs a valid element to render to. Please make sure Modal.CONTAINER_SELECTOR is set to a valid selector.`
            );
        }
        modalsContainerRef.current = modalContainerMountingElement;
    }, []);

    return {
        modalsContainerRef,
        modalRef,
        titleId,
        handleEntered,
        handleExited,
        handleOverlayClick,
    };
}
